import { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import { listQuotes, deleteQuote, type Quote, type QuoteFilters } from '@/api/quoteApi';
import { useAuth } from '@/hooks/useAuth';

const QUOTE_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired'] as const;

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  sent: 'processing',
  accepted: 'success',
  rejected: 'error',
  expired: 'warning',
};

export default function QuoteListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<QuoteFilters>({});

  const canWrite = hasPermission('quotes', 'write');
  const canDelete = hasPermission('quotes', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof QuoteFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => listQuotes(params, filters),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('quotes.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteQuote(id);
            message.success(t('quotes.delete_success'));
            gridRef.current?.reload();
          } catch (err) {
            message.error(err instanceof Error ? err.message : t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<Quote> = [
    {
      title: t('quotes.field.quote_number'),
      dataIndex: 'quote_number',
      key: 'quote_number',
      width: 140,
      render: (val: string) => val || '—',
    },
    {
      title: t('quotes.field.title'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: t('quotes.field.customer_id'),
      dataIndex: 'customer_id',
      key: 'customer_id',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('quotes.field.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (val: string) => (
        <Tag color={STATUS_COLORS[val] ?? 'default'}>
          {t(`quotes.status_options.${val}`)}
        </Tag>
      ),
    },
    {
      title: t('quotes.field.total'),
      dataIndex: 'total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (val: number, record: Quote) =>
        `${record.currency} ${val?.toLocaleString(undefined, { minimumFractionDigits: 2 }) ?? '0.00'}`,
    },
    {
      title: t('quotes.field.valid_until'),
      dataIndex: 'valid_until',
      key: 'valid_until',
      width: 130,
      render: (val: string | null) =>
        val ? new Date(val).toLocaleDateString() : '—',
    },
    {
      title: t('quotes.field.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (val: string) => new Date(val).toLocaleDateString(),
    },
    {
      title: t('app.actions'),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_: unknown, record: Quote) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/quotes/${record.id}`)}
          />
          {canWrite && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/quotes/${record.id}/edit`)}
            />
          )}
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 150 }}
            placeholder={t('quotes.filter.status')}
            allowClear
            value={filters.status}
            onChange={(val) => handleFilterChange('status', val)}
            options={QUOTE_STATUSES.map((s) => ({
              value: s,
              label: t(`quotes.status_options.${s}`),
            }))}
          />
        </Space>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/quotes/new')}
          >
            {t('quotes.new_quote')}
          </Button>
        )}
      </div>
      <DataGrid<Quote>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchPlaceholder={t('quotes.search_placeholder')}
      />
    </div>
  );
}
